<template>
  <div class="ptos">
    <page-heading title="PTOs"></page-heading>

    <content-placeholder v-if="fetching">
      <el-card>
        <p class="text text-10 text-h-20"></p>
        <p class="text text-full"></p>
        <p class="text text-full"></p>
        <p class="text text-50"></p>
        <p class="text text-25"></p>
      </el-card>
    </content-placeholder>

    <div v-else>
      <el-card>
        <div slot="header">
          <span>Pending PTOs</span>
        </div>
        <div slot="footer">
          Total: {{ pendingTotal }}
        </div>
        <ptos-list :ptos="pendingPtos"></ptos-list>
        <div class="pagination" v-if="pendingTotal > pendingPageSize">
          <el-pagination
            @current-change="handlePendingPageChange"
            :current-page.sync="pendingPage"
            :page-size="pendingPageSize"
            layout="total, prev, pager, next"
            :total="pendingTotal">
          </el-pagination>
        </div>
      </el-card>
      <el-card v-if="fetchingAll">
        <p class="text text-10 text-h-20"></p>
        <p class="text text-full"></p>
        <p class="text text-full"></p>
        <p class="text text-50"></p>
        <p class="text text-25"></p>
      </el-card>
      <el-card v-else>
        <el-tabs v-model="activeName">
          <div class="between">
            <el-input type="text" v-model.lazy="filter" class="ptos_name_search" :placeholder="$t('modules.ptoAvailability.fields.search.placeholder')"></el-input>
            <el-date-picker type="year" class="yearPicker" v-model.lazy.trim="year"  format="yyyy" :picker-options="{format:'yyyy'}" :placeholder="$t('modules.ptoAvailability.fields.search.year')"></el-date-picker>
          </div>
          <el-tab-pane :label="`Rejected PTOs`" name="second">
            <el-card>
              <div slot="header">
                <span>Rejected PTOs</span>
              </div>
              <div slot="footer">
                Total: {{ rejectedTotal }}
              </div>
              <ptos-list :ptos="rejectedPtos"></ptos-list>
              <div class="pagination" v-if="rejectedTotal > rejectedPageSize">
                <el-pagination
                  @current-change="handleRejectedPageChange"
                  :current-page.sync="rejectedPage"
                  :page-size="rejectedPageSize"
                  layout="total, prev, pager, next"
                  :total="rejectedTotal">
                </el-pagination>
              </div>
            </el-card>
          </el-tab-pane>
          <el-tab-pane :label="`Approved PTOs`" name="third">
            <el-card>
              <div slot="header">
                <span>Approved PTOs</span>
              </div>
              <div slot="footer">
                Total: {{ approvedTotal }}
              </div>
              <ptos-list :ptos="approvedPtos"></ptos-list>
              <div class="pagination" v-if="approvedTotal > approvedPageSize">
                <el-pagination
                  @current-change="handleApprovedPageChange"
                  :current-page.sync="approvedPage"
                  :page-size="approvedPageSize"
                  layout="total, prev, pager, next"
                  :total="approvedTotal">
                </el-pagination>
              </div>
            </el-card>
          </el-tab-pane>
        </el-tabs>
      </el-card>
      <pto-form-modal />
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';

  import PtosList from '@/components/pto/PtosList';
  import PtoFormModal from '@/components/pto/PtoFormModal';

  export default {
    name: 'PtosPage',
    components: {
      ptoFormModal: PtoFormModal,
      ptosList: PtosList,
    },
    data() {
      return {
        activeName: 'third',
        translationPrefix: 'pages.ptoRequests',
        filter: '',
        year: '',
        pendingPtos: [],
        rejectedPtos: [],
        approvedPtos: [],
        fetchingAll: true,
        pendingPage: 1,
        pendingPageSize: 20,
        pendingTotal: 0,
        rejectedPage: 1,
        rejectedPageSize: 20,
        rejectedTotal: 0,
        approvedPage: 1,
        approvedPageSize: 20,
        approvedTotal: 0,
      };
    },
    created() {
      this.fetchPendingPtos();
      this.fetchAllPtos();
    },
    watch: {
      activeName() {
        this.filter = '';
        this.year = '';
      },
      filter() {
        this.rejectedPage = 1;
        this.approvedPage = 1;
        this.fetchAllPtos();
      },
      year() {
        this.rejectedPage = 1;
        this.approvedPage = 1;
        this.fetchAllPtos();
      },
    },

    computed: {
      ...mapGetters('entities/ptos', ['fetching']),
      ...mapGetters('entities/users', ['currentUser']),
    },
    methods: {
      ...mapActions('entities/ptos', ['fetchPtos', 'openFormModal']),

      buildRejectedParams() {
        const params = {
          page: this.rejectedPage,
          per_page: this.rejectedPageSize,
        };
        return params;
      },

      buildApprovedParams() {
        const params = {
          page: this.approvedPage,
          per_page: this.approvedPageSize,
          status: 'approved',
        };
        return params;
      },

      buildPendingParams() {
        const params = {
          page: this.pendingPage,
          per_page: this.pendingPageSize,
        };
        return params;
      },

      fetchAllPtos() {
        this.fetchingAll = true;
        Promise.all([
          this.fetchRejectedPtos(),
          this.fetchApprovedPtos(),
        ]).finally(() => {
          this.fetchingAll = false;
        });
      },

      fetchPendingPtos() {
        this.$store.dispatch('entities/ptos/fetchPendingPtos', this.buildPendingParams())
          .then((response) => {
            if (response.status) {
              this.pendingPtos = response.data.entities;
              this.pendingTotal = response.data.meta.total_entries;
            }
          });
      },

      fetchRejectedPtos() {
        return this.$store.dispatch('entities/ptos/fetchRejectedPtos', this.buildRejectedParams())
          .then((response) => {
            if (response.status) {
              this.rejectedPtos = response.data.entities.sort((a, b) =>
                new Date(b.created_at) - new Date(a.created_at),
              );
              this.rejectedTotal = response.data.meta.total_entries;
              this.applyFilters();
            }
          });
      },

      fetchApprovedPtos() {
        return this.fetchPtos(this.buildApprovedParams())
          .then((response) => {
            if (response.status) {
              this.approvedPtos = response.data.entities.sort((a, b) =>
                new Date(b.created_at) - new Date(a.created_at),
              );
              this.approvedTotal = response.data.meta.total_entries;
              this.applyFilters();
            }
          });
      },

      applyFilters() {
        // Apply client-side filtering for name and year
        if (this.filter.length > 0) {
          this.rejectedPtos = this.rejectedPtos.filter(data =>
            data.user && data.user.profile &&
            data.user.profile.full_name.toLowerCase().includes(this.filter.toLowerCase()),
          );
          this.approvedPtos = this.approvedPtos.filter(data =>
            data.user && data.user.profile &&
            data.user.profile.full_name.toLowerCase().includes(this.filter.toLowerCase()),
          );
        }

        const year = new Date(this.year).getFullYear();
        if (year && year !== 1970) {
          this.rejectedPtos = this.rejectedPtos.filter(data =>
            new Date(data.created_at).getFullYear() === year,
          );
          this.approvedPtos = this.approvedPtos.filter(data =>
            new Date(data.created_at).getFullYear() === year,
          );
        }

        // Re-sort after filtering to maintain newest first order
        this.rejectedPtos.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        this.approvedPtos.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      },

      handlePendingPageChange(val) {
        this.pendingPage = val;
        this.fetchPendingPtos();
      },

      handleRejectedPageChange(val) {
        this.rejectedPage = val;
        this.fetchRejectedPtos();
      },

      handleApprovedPageChange(val) {
        this.approvedPage = val;
        this.fetchApprovedPtos();
      },
    },
  };
</script>

<style lang="scss">
  .el-tabs__content{
    overflow: visible;
  }

  .ptos_name_search {
    max-width: 300px;
    width: 300px;
    margin-right: 10px;
  }

  .between{
    display: flex;
    justify-content: left;
    align-items: center;
    margin-bottom: 10px;
  }

  .yearPicker{
    width: auto !important;
  }

  .pagination {
    margin-top: 20px;
    text-align: center;
  }
</style>



// WEBPACK FOOTER //
// src/pages/Ptos.vue